// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import { formatUsDateLocal } from '../../api/format'
import {
  KeyRound, Plus, Trash2, Copy, Loader2, AlertCircle,
  CheckCircle2, ShieldAlert, Clock, X,
} from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { useAuth } from '../../auth/AuthContext'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'

// D-UI-2: what each column shows, for its header funnel.
const API_KEY_FUNNELS: FunnelColumn<api.ApiKeySummary>[] = [
  { key: 'name',    label: 'Name',      value: k => k.name },
  { key: 'prefix',  label: 'Prefix',    value: k => `${k.key_prefix}…` },
  { key: 'scopes',  label: 'Scopes',    value: k => k.scopes === null ? 'all (legacy)' : `${k.scopes.length} perm${k.scopes.length === 1 ? '' : 's'}` },
  { key: 'created', label: 'Created',   value: k => k.created_at },
  { key: 'expires', label: 'Expires',   value: k => k.expires_at },
  { key: 'used',    label: 'Last used', value: k => k.last_used_at },
  { key: 'status',  label: 'Status',    value: k => k.revoked_at ? 'Revoked' : k.expires_at && new Date(k.expires_at) <= new Date() ? 'Expired' : 'Active' },
]

/**
 * /admin/api-keys — WI-981 (moved from /settings/api-keys per sponsor 2026-05-10)
 *
 * SaaS system-level API key management.
 *
 * This is the platform admin's view: who in the system has issued an API
 * key, and the controls to issue / revoke them. v1 lists only the
 * authenticated user's own keys (typical use: an admin issuing keys for
 * external automation that runs as that admin's account).
 *
 * A separate pharmacy-level page (per-pharmacy keys, scoped to one
 * pharmacy's data) is planned but out of scope for this WI — this page
 * is the system-level surface only.
 *
 * Backend: /api/ops/api-keys (WI-980).
 *
 * Permission gates (UI mirrors what the backend enforces):
 *   account.api_keys.self    → see/issue/revoke own keys
 *   admin.api_keys.manage    → see/issue/revoke any account's keys (the cross-account
 *                              admin listing isn't built yet; v1 is self-only even
 *                              for users who hold the manage permission)
 *
 * This page does NOT bypass the backend's permission checks. If the user
 * lacks both permissions, the API will return 403 and the page shows that.
 *
 * Plaintext keys are only ever returned once — at issue. The page surfaces
 * a one-time reveal panel after creation. If you close it without copying,
 * you have to revoke + reissue.
 */
export default function ApiKeysAdmin() {
  const { me } = useAuth()
  const has = (p: string) => Boolean(me?.permissions?.includes(p))

  // Permission gate at the page level — soft-deny with a friendly message.
  // The backend enforces too, but this avoids confusing API errors for
  // users who shouldn't see the page at all.
  const canManage = has('account.api_keys.self') || has('admin.api_keys.manage')

  const [keys, setKeys] = useState<api.ApiKeySummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Issue dialog state
  const [showIssue, setShowIssue] = useState(false)
  const [issueName, setIssueName] = useState('')
  const [issueExpiresInDays, setIssueExpiresInDays] = useState<string>('365')
  const [issuing, setIssuing] = useState(false)

  // WI-982: per-key permission scope picker
  const [availablePerms, setAvailablePerms] = useState<string[] | null>(null)
  const [permsLoading, setPermsLoading] = useState(false)
  const [permsError, setPermsError] = useState<string | null>(null)
  const [selectedScopes, setSelectedScopes] = useState<Set<string>>(new Set())
  const [permFilter, setPermFilter] = useState('')

  // One-time reveal of plaintext after issue
  const [reveal, setReveal] = useState<api.ApiKeyIssueResponse | null>(null)
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle')

  // Revoke confirmation
  const [revokeTarget, setRevokeTarget] = useState<api.ApiKeySummary | null>(null)
  const [revokeReason, setRevokeReason] = useState('')
  const [revoking, setRevoking] = useState(false)

  async function reload() {
    setLoading(true); setError(null)
    try {
      const r = await api.listApiKeys()
      setKeys(r.items)
    } catch (e) {
      setError(friendlyError(e))
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { if (canManage) reload() }, [canManage])

  // The header funnels filter the whole loaded list.
  const fx = useTableFunnels(keys, API_KEY_FUNNELS)

  async function openIssueDialog() {
    setShowIssue(true)
    setIssueName('')
    setIssueExpiresInDays('365')
    setSelectedScopes(new Set())
    setPermFilter('')
    setPermsError(null)
    if (availablePerms === null) {
      setPermsLoading(true)
      try {
        const r = await api.listAvailableApiKeyPermissions()
        setAvailablePerms(r.permissions)
      } catch (e) {
        setPermsError(friendlyError(e))
      } finally {
        setPermsLoading(false)
      }
    }
  }

  async function handleIssue() {
    if (!issueName.trim()) return
    if (selectedScopes.size === 0) return
    setIssuing(true); setError(null)
    try {
      let expiresAt: string | undefined
      const days = parseInt(issueExpiresInDays, 10)
      if (!Number.isNaN(days) && days > 0) {
        const d = new Date()
        d.setDate(d.getDate() + days)
        expiresAt = d.toISOString()
      }
      const created = await api.issueApiKey({
        name: issueName.trim(),
        expiresAt,
        scopes: Array.from(selectedScopes),
      })
      setReveal(created)
      setShowIssue(false)
      setIssueName('')
      setSelectedScopes(new Set())
      await reload()
    } catch (e) {
      setError(friendlyError(e))
    } finally {
      setIssuing(false)
    }
  }

  async function handleRevoke() {
    if (!revokeTarget) return
    setRevoking(true); setError(null)
    try {
      await api.revokeApiKey(revokeTarget.id, revokeReason.trim() || undefined)
      setRevokeTarget(null)
      setRevokeReason('')
      await reload()
    } catch (e) {
      setError(friendlyError(e))
    } finally {
      setRevoking(false)
    }
  }

  async function handleCopy(text: string) {
    try {
      await navigator.clipboard.writeText(text)
      setCopyState('copied')
      setTimeout(() => setCopyState('idle'), 2000)
    } catch {
      // Fallback: select text for the user (best-effort)
    }
  }

  // ----- RENDER -----

  if (!canManage) {
    return (
      <div style={{ maxWidth: '100%' }}>
        <h1 style={titleStyle}><KeyRound size={22} style={iconStyle} /> API keys</h1>
        <div style={denyBox}>
          <ShieldAlert size={18} />
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>You don't have permission to manage API keys.</div>
            <div style={{ fontSize: 13, color: 'var(--color-text-soft)' }}>
              You need either <code>account.api_keys.self</code> or <code>admin.api_keys.manage</code>.
              Ask a platform admin to grant you one of these permissions on a role you hold.
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 'var(--page-max, 960px)' }}>
      <header style={{ marginBottom: 16 }}>
        <h1 style={titleStyle}>
          <KeyRound size={22} style={iconStyle} /> API keys
        </h1>
        <p style={subtitleStyle}>
          Programmatic access tokens. Use the <code>Authorization: Bearer …</code> header
          on requests to <code>/api/*</code> endpoints. Each key inherits the permissions
          of the account it's issued to.
        </p>
      </header>

      <div style={toolbarStyle}>
        <button
          onClick={openIssueDialog}
          disabled={Boolean(reveal)}
          style={primaryBtnStyle}
        >
          <Plus size={14} /> New key
        </button>
        <span style={{ fontSize: 12, color: 'var(--color-text-mute)', marginLeft: 'auto' }}>
          {keys.length} key{keys.length === 1 ? '' : 's'}
        </span>
      </div>

      {error && (
        <div style={errorBox}>
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* One-time reveal panel */}
      {reveal && (
        <div style={revealBox}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <CheckCircle2 size={20} style={{ color: '#16a34a', flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                Key issued: {reveal.name}
              </div>
              <div style={{ fontSize: 13, color: 'var(--color-text-soft)', marginBottom: 12 }}>
                Copy this key now. The full key will <strong>never</strong> be shown again — only its prefix.
              </div>
              <div style={plaintextRow}>
                <code style={plaintextCode}>{reveal.plaintext}</code>
                <button onClick={() => handleCopy(reveal.plaintext)} style={copyBtnStyle}>
                  <Copy size={13} />
                  {copyState === 'copied' ? 'Copied' : 'Copy'}
                </button>
              </div>
              <button
                onClick={() => { setReveal(null); setCopyState('idle') }}
                style={{ ...secondaryBtnStyle, marginTop: 12 }}
              >
                I've stored it — dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div style={loadingBox}><Loader2 size={16} className="spin" /> Loading keys…</div>
      ) : keys.length === 0 ? (
        <div style={emptyBox}>
          <KeyRound size={28} style={{ color: 'var(--color-text-mute)', marginBottom: 8 }} />
          <div style={{ fontSize: 14, marginBottom: 4 }}>No API keys yet.</div>
          <div style={{ fontSize: 13, color: 'var(--color-text-soft)' }}>
            Click <strong>New key</strong> to issue your first one.
          </div>
        </div>
      ) : (
        <>
        {fx.chips}
        <div style={tableWrap}>
          <table {...fx.tableProps} style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Name {fx.funnel('name')}</th>
                <th style={thStyle}>Prefix {fx.funnel('prefix')}</th>
                <th style={thStyle}>Scopes {fx.funnel('scopes')}</th>
                <th style={thStyle}>Created {fx.funnel('created')}</th>
                <th style={thStyle}>Expires {fx.funnel('expires')}</th>
                <th style={thStyle}>Last used {fx.funnel('used')}</th>
                <th style={thStyle}>Status {fx.funnel('status')}</th>
                <th style={{ ...thStyle, width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {fx.rows.map(k => (
                <tr key={k.id} style={{ opacity: k.is_active ? 1 : 0.55 }}>
                  <td style={tdStyle}>{k.name}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12 }}>{k.key_prefix}…</td>
                  <td style={tdStyle}>
                    {k.scopes === null ? (
                      <span title="Inherits all account permissions" style={mutedStyle}>all (legacy)</span>
                    ) : (
                      <span title={k.scopes.join('\n')}>
                        {k.scopes.length} perm{k.scopes.length === 1 ? '' : 's'}
                      </span>
                    )}
                  </td>
                  <td style={tdStyle}>{fmtDate(k.created_at)}</td>
                  <td style={tdStyle}>{k.expires_at ? fmtDate(k.expires_at) : <span style={mutedStyle}>—</span>}</td>
                  <td style={tdStyle}>
                    {k.last_used_at ? (
                      <span title={k.last_used_ip ? `from ${k.last_used_ip}` : undefined}>
                        {fmtRelative(k.last_used_at)}
                      </span>
                    ) : (
                      <span style={mutedStyle}>Never</span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {k.revoked_at ? (
                      <span style={badgeRevokedStyle}>Revoked</span>
                    ) : k.expires_at && new Date(k.expires_at) <= new Date() ? (
                      <span style={badgeExpiredStyle}>Expired</span>
                    ) : (
                      <span style={badgeActiveStyle}>Active</span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {k.is_active && (
                      <button onClick={() => setRevokeTarget(k)} style={iconBtnStyle} title="Revoke">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      {/* Issue dialog */}
      {showIssue && (
        <Modal onClose={() => setShowIssue(false)} title="Issue new API key">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={labelStyle}>
              Name
              <input
                type="text"
                placeholder="e.g. claude-bot, jenkins-roadmap-sync"
                value={issueName}
                onChange={e => setIssueName(e.target.value)}
                maxLength={128}
                style={inputStyle}
                autoFocus
              />
              <span style={hintStyle}>
                A short label so you remember what this key is for. Max 128 chars.
              </span>
            </label>
            <label style={labelStyle}>
              Expires in
              <select
                value={issueExpiresInDays}
                onChange={e => setIssueExpiresInDays(e.target.value)}
                style={inputStyle}
              >
                <option value="30">30 days</option>
                <option value="90">90 days</option>
                <option value="365">1 year (default)</option>
                <option value="1825">5 years (max)</option>
              </select>
              <span style={hintStyle}>
                <Clock size={11} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                After this date the key stops working. You can always revoke earlier.
              </span>
            </label>

            <div style={labelStyle}>
              Permissions ({selectedScopes.size} selected)
              <span style={hintStyle}>
                Pick exactly what this key can do. The key can never have permissions
                you don't currently hold.
              </span>
              {permsLoading ? (
                <div style={{ ...permPickerBox, justifyContent: 'center', alignItems: 'center', display: 'flex' }}>
                  <Loader2 size={14} className="spin" />
                  <span style={{ marginLeft: 6, fontSize: 13, color: 'var(--color-text-soft)' }}>
                    Loading permissions…
                  </span>
                </div>
              ) : permsError ? (
                <div style={errorBox}>
                  <AlertCircle size={14} /> {permsError}
                </div>
              ) : availablePerms && availablePerms.length === 0 ? (
                <div style={permPickerBox}>
                  <span style={{ fontSize: 13, color: 'var(--color-text-soft)' }}>
                    Your account has no permissions to grant.
                  </span>
                </div>
              ) : availablePerms ? (
                <>
                  <div style={pickerToolbarStyle}>
                    <input
                      type="text"
                      placeholder="Filter permissions…"
                      value={permFilter}
                      onChange={e => setPermFilter(e.target.value)}
                      style={{ ...inputStyle, padding: '6px 8px', fontSize: 12, flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={() => setSelectedScopes(new Set(availablePerms))}
                      style={{ ...secondaryBtnStyle, padding: '6px 10px', fontSize: 12 }}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedScopes(new Set())}
                      style={{ ...secondaryBtnStyle, padding: '6px 10px', fontSize: 12 }}
                    >
                      Clear
                    </button>
                  </div>
                  <div style={permPickerBox}>
                    {groupPermissions(availablePerms, permFilter).map(group => (
                      <div key={group.category} style={permGroupStyle}>
                        <div style={permGroupHeaderStyle}>{group.category}</div>
                        {group.perms.map(p => (
                          <label key={p} style={permRowStyle}>
                            <input
                              type="checkbox"
                              checked={selectedScopes.has(p)}
                              onChange={e => {
                                const next = new Set(selectedScopes)
                                if (e.target.checked) next.add(p); else next.delete(p)
                                setSelectedScopes(next)
                              }}
                            />
                            <code style={{ fontFamily: 'monospace', fontSize: 12 }}>{p}</code>
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                </>
              ) : null}
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
              <button onClick={() => setShowIssue(false)} style={secondaryBtnStyle} disabled={issuing}>
                Cancel
              </button>
              <button
                onClick={handleIssue}
                style={primaryBtnStyle}
                disabled={issuing || !issueName.trim() || selectedScopes.size === 0}
                title={selectedScopes.size === 0 ? 'Select at least one permission' : ''}
              >
                {issuing ? <Loader2 size={14} className="spin" /> : <KeyRound size={14} />}
                Issue key
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Revoke confirmation */}
      {revokeTarget && (
        <Modal onClose={() => setRevokeTarget(null)} title={`Revoke "${revokeTarget.name}"?`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text-soft)' }}>
              The key <code style={{ fontFamily: 'monospace', fontSize: 12 }}>{revokeTarget.key_prefix}…</code>{' '}
              will stop working immediately. This action cannot be undone — to grant access
              again you'll need to issue a new key.
            </p>
            <label style={labelStyle}>
              Reason (optional)
              <input
                type="text"
                placeholder="e.g. rotated, compromised, no longer needed"
                value={revokeReason}
                onChange={e => setRevokeReason(e.target.value)}
                maxLength={256}
                style={inputStyle}
              />
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
              <button onClick={() => setRevokeTarget(null)} style={secondaryBtnStyle} disabled={revoking}>
                Cancel
              </button>
              <button onClick={handleRevoke} style={dangerBtnStyle} disabled={revoking}>
                {revoking ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />}
                Revoke key
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

// ============================================================
// Helpers
// ============================================================

function fmtDate(iso: string): string {
  return formatUsDateLocal(iso)
}

function fmtRelative(iso: string): string {
  const d = new Date(iso)
  const diff = Date.now() - d.getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  return fmtDate(iso)
}

/**
 * Group a flat permission list by category prefix (the part before the first '.').
 * Filters by case-insensitive substring match on the full key.
 */
function groupPermissions(perms: string[], filter: string): { category: string; perms: string[] }[] {
  const f = filter.trim().toLowerCase()
  const filtered = f ? perms.filter(p => p.toLowerCase().includes(f)) : perms
  const groups = new Map<string, string[]>()
  for (const p of filtered) {
    const dot = p.indexOf('.')
    const cat = dot > 0 ? p.substring(0, dot) : '(other)'
    if (!groups.has(cat)) groups.set(cat, [])
    groups.get(cat)!.push(p)
  }
  return Array.from(groups.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([category, perms]) => ({ category, perms: perms.sort() }))
}

interface ModalProps {
  title: string
  onClose: () => void
  children: React.ReactNode
}
function Modal({ title, onClose, children }: ModalProps) {
  return (
    <div onClick={onClose} style={modalBackdrop}>
      <div onClick={e => e.stopPropagation()} style={modalCard}>
        <div style={modalHeader}>
          <span style={modalTitle}>{title}</span>
          <button onClick={onClose} style={iconBtnStyle} aria-label="Close"><X size={16} /></button>
        </div>
        <div style={modalBody}>{children}</div>
      </div>
    </div>
  )
}

// ============================================================
// Styles
// ============================================================

const titleStyle: React.CSSProperties = {
  fontFamily: 'Georgia, "Source Serif Pro", serif',
  fontSize: 26, fontWeight: 700,
  margin: 0, letterSpacing: -0.5,
  display: 'flex', alignItems: 'center', gap: 10,
}
const iconStyle: React.CSSProperties = { color: 'var(--color-text-soft)' }
const subtitleStyle: React.CSSProperties = {
  marginTop: 8, marginBottom: 0,
  fontSize: 14, color: 'var(--color-text-soft)', maxWidth: '100%', lineHeight: 1.5,
}
const toolbarStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  marginTop: 24, marginBottom: 16,
}
const primaryBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', fontSize: 13, fontWeight: 500,
  background: 'var(--color-primary)', color: '#fff',
  border: '1px solid var(--color-primary)', borderRadius: 6, cursor: 'pointer',
}
const secondaryBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', fontSize: 13, fontWeight: 500,
  background: 'var(--color-surface, #fff)', color: 'var(--color-text)',
  border: '1px solid var(--color-border)', borderRadius: 6, cursor: 'pointer',
}
const dangerBtnStyle: React.CSSProperties = {
  ...primaryBtnStyle,
  background: '#dc2626', borderColor: '#dc2626',
}
const iconBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 28, height: 28, padding: 0,
  background: 'transparent', color: 'var(--color-text-soft)',
  border: '1px solid transparent', borderRadius: 4, cursor: 'pointer',
}
const copyBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '4px 10px', fontSize: 12,
  background: 'var(--color-surface, #fff)', color: 'var(--color-text)',
  border: '1px solid var(--color-border)', borderRadius: 4, cursor: 'pointer',
  flexShrink: 0,
}
const tableWrap: React.CSSProperties = {
  background: 'var(--color-surface, #fff)',
  border: '1px solid var(--color-border)',
  borderRadius: 8, overflowX: 'auto', // was overflow:hidden — clipped wide tables with no scrollbar
}
const tableStyle: React.CSSProperties = {
  width: '100%', borderCollapse: 'collapse', fontSize: 13,
}
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '10px 12px',
  background: '#fafafa',
  borderBottom: '1px solid var(--color-border)',
  fontWeight: 600, fontSize: 12,
  color: 'var(--color-text-soft)',
}
const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  borderBottom: '1px solid var(--color-border)',
}
const mutedStyle: React.CSSProperties = { color: 'var(--color-text-mute)' }
const badgeActiveStyle: React.CSSProperties = {
  display: 'inline-block', padding: '2px 8px', fontSize: 11, fontWeight: 600,
  background: '#dcfce7', color: '#166534', borderRadius: 12,
}
const badgeRevokedStyle: React.CSSProperties = {
  display: 'inline-block', padding: '2px 8px', fontSize: 11, fontWeight: 600,
  background: '#fee2e2', color: '#991b1b', borderRadius: 12,
}
const badgeExpiredStyle: React.CSSProperties = {
  display: 'inline-block', padding: '2px 8px', fontSize: 11, fontWeight: 600,
  background: '#fef3c7', color: '#92400e', borderRadius: 12,
}
const denyBox: React.CSSProperties = {
  marginTop: 24,
  padding: 16, display: 'flex', alignItems: 'flex-start', gap: 12,
  background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8,
  color: '#78350f',
}
const errorBox: React.CSSProperties = {
  padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8,
  background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6,
  color: '#991b1b', fontSize: 13, marginBottom: 12,
}
const loadingBox: React.CSSProperties = {
  padding: 24, display: 'flex', alignItems: 'center', gap: 8,
  color: 'var(--color-text-soft)', fontSize: 13,
}
const emptyBox: React.CSSProperties = {
  padding: 48, textAlign: 'center',
  background: 'var(--color-surface, #fff)',
  border: '1px dashed var(--color-border)', borderRadius: 8,
}
const revealBox: React.CSSProperties = {
  padding: 16,
  background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8,
  marginBottom: 16,
}
const plaintextRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  background: '#fff', border: '1px solid var(--color-border)', borderRadius: 6,
  padding: '8px 10px',
}
const plaintextCode: React.CSSProperties = {
  flex: 1, fontFamily: 'monospace', fontSize: 13,
  wordBreak: 'break-all',
}
const labelStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4,
  fontSize: 13, fontWeight: 500,
}
const inputStyle: React.CSSProperties = {
  padding: '8px 10px', fontSize: 13,
  border: '1px solid var(--color-border)', borderRadius: 6,
  background: 'var(--color-surface, #fff)', color: 'var(--color-text)',
}
const hintStyle: React.CSSProperties = {
  fontSize: 12, color: 'var(--color-text-soft)', fontWeight: 400,
  display: 'flex', alignItems: 'center',
}
const modalBackdrop: React.CSSProperties = {
  position: 'fixed', inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000, padding: 16,
}
const modalCard: React.CSSProperties = {
  background: 'var(--color-surface, #fff)',
  border: '1px solid var(--color-border)', borderRadius: 10,
  boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  width: '100%', maxWidth: 480,
  display: 'flex', flexDirection: 'column',
  maxHeight: '90vh',
}
const modalHeader: React.CSSProperties = {
  padding: '14px 16px',
  borderBottom: '1px solid var(--color-border)',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
}
const modalTitle: React.CSSProperties = {
  fontSize: 15, fontWeight: 600,
}
const modalBody: React.CSSProperties = { padding: 16, overflowY: 'auto' }
const permPickerBox: React.CSSProperties = {
  border: '1px solid var(--color-border)', borderRadius: 6,
  padding: 8, maxHeight: 240, overflowY: 'auto',
  background: '#fafafa', display: 'flex', flexDirection: 'column', gap: 10,
}
const pickerToolbarStyle: React.CSSProperties = {
  display: 'flex', gap: 6, alignItems: 'center',
}
const permGroupStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4,
}
const permGroupHeaderStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
  color: 'var(--color-text-soft)', letterSpacing: 0.4,
  padding: '4px 0', borderBottom: '1px solid var(--color-border)',
}
const permRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '3px 4px', cursor: 'pointer', fontWeight: 400,
  fontSize: 12,
}
